import React from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import chapters from "../data/experience.json";
import { profile } from "../data/profile";
import "../styles/Page.scss";

const ease = [0.22, 1, 0.36, 1];

export function Reveal({ children, className = "", delay = 0, as = "div", ...rest }) {
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      className={className}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease, delay }}
      {...rest}
    >
      {children}
    </Comp>
  );
}

export function SectionHeading({ eyebrow, title, children }) {
  return (
    <Reveal className="section-heading">
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </Reveal>
  );
}

export function Tags({ items, className = "" }) {
  return (
    <ul className={`tags ${className}`}>
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}

function PageShell({ slug, children }) {
  const idx = chapters.findIndex((c) => c.name.toLowerCase() === slug);
  const chapter = chapters[idx];
  const next = chapters[(idx + 1) % chapters.length];

  return (
    <motion.div
      className="page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.25 } }}
    >
      <header className="topbar">
        <Link to="/" className="topbar-mark" aria-label="Back to home">
          <span className="topbar-back" aria-hidden="true">←</span>
          <span className="topbar-initials">{profile.initials}</span>
          <span className="topbar-name">{profile.name}</span>
        </Link>
        <nav className="topbar-nav" aria-label="Sections">
          {chapters.map((c) => (
            <NavLink key={c.no} to={`/${c.name.toLowerCase()}`}>
              <span>{c.no}</span>
              {c.name}
            </NavLink>
          ))}
        </nav>
      </header>

      <section className="page-hero">
        <div className="page-hero-inner">
          <motion.div
            className="page-hero-index"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.1 }}
          >
            {chapter.no} / {chapters.length.toString().padStart(2, "0")}
          </motion.div>
          <h1 className="page-hero-title" style={{ "--len": chapter.name.length }}>
            <motion.span
              initial={{ y: "105%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.15 }}
            >
              {chapter.name}
            </motion.span>
          </h1>
          <motion.p
            className="page-hero-kicker"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {chapter.kicker}
          </motion.p>
        </div>
      </section>

      <main className="page-body">{children}</main>

      <footer className="page-footer">
        <Link to={`/${next.name.toLowerCase()}`} className="next-chapter">
          <span className="eyebrow">Next chapter · {next.no}</span>
          <span className="next-chapter-title" style={{ "--len": next.name.length + 2 }}>
            {next.name} <span aria-hidden="true">→</span>
          </span>
        </Link>
        <div className="footer-meta">
          <span>
            © {new Date().getFullYear()} {profile.name}
          </span>
          <span className="footer-links">
            <a href={`mailto:${profile.email}`}>{profile.email}</a>
            <a href={profile.github} target="_blank" rel="noopener noreferrer">
              GitHub
            </a>
            <a href={profile.linkedin} target="_blank" rel="noopener noreferrer">
              LinkedIn
            </a>
          </span>
        </div>
      </footer>
    </motion.div>
  );
}

export default PageShell;
